import { lazy, Suspense, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Spin } from '@/ui/compat/antd'
import { useSearchParams } from 'react-router-dom'

import Recherche from '../../admin/modules/GestionCartesAccesEtBienEtre/Recherche'
import { CARD_TYPE } from '../../admin/modules/GestionCartesAccesEtBienEtre/shared/constants'
import AmicaleMoteurTabs, { AMICALE_MOTEUR_TAB } from '../components/AmicaleMoteurTabs'
import useAmicale from '../hooks/useAmicale'
import useAmicaleCartesAccess from '../hooks/useAmicaleCartesAccess'

const MoteurReservation = lazy(() =>
  import('../../admin/modules/GestionHotel/MoteurReservation')
)

const PageFallback = () => {
  const { t } = useTranslation()
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 80px)',
      }}
      aria-label={t('amicales.moteur.loadingAria')}
      role="status"
    >
      <Spin size="large" />
    </div>
  )
}

const MoteurAmicalesPage = () => {
  const { client } = useAmicale()
  const [searchParams, setSearchParams] = useSearchParams()
  const { loading, showTabs, hasCarteAcces, hasBienEtre } = useAmicaleCartesAccess(Boolean(client?.id))

  const allowedTabs = useMemo(() => {
    const tabs = [AMICALE_MOTEUR_TAB.HOTEL]
    if (hasCarteAcces) tabs.push(AMICALE_MOTEUR_TAB.CARTE_ACCES)
    if (hasBienEtre) tabs.push(AMICALE_MOTEUR_TAB.BIEN_ETRE)
    return tabs
  }, [hasCarteAcces, hasBienEtre])

  const rawTab = searchParams.get('tab') || AMICALE_MOTEUR_TAB.HOTEL
  const activeTab = allowedTabs.includes(rawTab) ? rawTab : AMICALE_MOTEUR_TAB.HOTEL

  useEffect(() => {
    if (loading || !showTabs) return
    if (rawTab !== activeTab) {
      const next = new URLSearchParams(searchParams)
      next.set('tab', activeTab)
      setSearchParams(next, { replace: true })
    }
  }, [loading, showTabs, rawTab, activeTab, searchParams, setSearchParams])

  const handleTabChange = (tab) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  const renderContent = () => {
    if (activeTab === AMICALE_MOTEUR_TAB.CARTE_ACCES) {
      return (
        <Recherche
          embed
          isAmical
          hideTypeFilter
          fixedType={CARD_TYPE.ACCES}
          partnerClientId={client?.id}
          urlKeepKeys={['tab']}
        />
      )
    }
    if (activeTab === AMICALE_MOTEUR_TAB.BIEN_ETRE) {
      return (
        <Recherche
          embed
          isAmical
          hideTypeFilter
          fixedType={CARD_TYPE.BIEN_ETRE}
          partnerClientId={client?.id}
          urlKeepKeys={['tab']}
        />
      )
    }
    return (
      <Suspense fallback={<PageFallback />}>
        <MoteurReservation />
      </Suspense>
    )
  }

  return (
    <div className="w-full max-w-[1600px] mx-auto px-0 md:px-6">
      {!loading && showTabs ? (
        <AmicaleMoteurTabs
          activeTab={activeTab}
          onChange={handleTabChange}
          hasCarteAcces={hasCarteAcces}
          hasBienEtre={hasBienEtre}
        />
      ) : null}
      {renderContent()}
    </div>
  )
}

export default MoteurAmicalesPage
